// @ts-check
// Cartão de widget (doc 16, 10.3 e 10.9): título, menu (exportar CSV e, em edição, editar, duplicar, mover para outra
// aba e remover), estados de carregando, configuração incompleta, vazio e erro, e a visualização conforme o tipo e os
// dados. Widget com filtros próprios mostra um ícone ao lado do título. Fora da edição, os pontos com registros abrem
// a lista filtrada (contrato 07, seção 10): o controle do clique vai para a visualização por contexto.
import { html } from 'htm/preact';
import { useMemo, useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { getErrorMessage } from '../../data/api-client.js';
import { downloadCsv, toFileName } from '../../lib/csv.js';
import { BarChart } from './bar-chart.js';
import { LineChart } from './line-chart.js';
import { PieChart } from './pie-chart.js';
import { IframeWidget, NumberWidget, TableWidget, TextWidget } from './simple-widgets.js';
import { hasWidgetFilters } from '../../../../shared/dashboards/widget-filters.js';
import {
  BarListWidget, DistributionWidget, FunnelFlowWidget, LEAD_QUALITY_REPORTS, StageFlowWidget, StatGroupWidget, getReportSubtitle, isReportWidgetType,
} from './report-widgets.js';
import { GeoMapWidget } from './geo-map-widget.js';
import { WidgetDescription } from './widget-description.js';
import { getKpiMetric } from './widget-format.js';
import { isWidgetDataEmpty, useWidgetData, widgetDataToCsv } from './widget-data.js';
import { DrilldownContext, createDrillController } from './drilldown.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/dashboards.js').DashboardWidget} DashboardWidget
 * @typedef {import('../../data/dashboards.js').WidgetData} WidgetData
 * @typedef {import('../../data/dashboards.js').DashboardRequestFilters} DashboardRequestFilters
 */

/**
 * @param {{ widget: DashboardWidget, data: WidgetData, rowSpan: number, isEditing?: boolean }} props
 */
export function WidgetVisualization({ widget, data, rowSpan, isEditing }) {
  const configuration = widget.configuration ?? {};
  const kpiMetric = widget.type === 'KPI' ? getKpiMetric(configuration.metric) : undefined;
  switch (data.kind) {
    case 'NUMBER':
      return html`<${NumberWidget}
        data=${{ ...data, format: configuration.format ?? data.format }}
        prefix=${configuration.prefix ?? ''}
        suffix=${configuration.suffix ?? ''}
        lowerIsBetter=${Boolean(kpiMetric?.lowerIsBetter)}
        compact=${rowSpan <= 2}
      />`;
    case 'SERIES': {
      const isLine = widget.type === 'LINE' || kpiMetric?.visualization === 'LINE';
      if (isLine) {
        return html`<${LineChart}
          data=${data}
          showDataLabels=${Boolean(configuration.showDataLabels)}
          isCumulative=${Boolean(configuration.isCumulative)}
          colorName=${configuration.color}
          hasComparison=${widget.type === 'KPI'}
        />`;
      }
      const isHorizontal = configuration.layout === 'HORIZONTAL' || kpiMetric?.visualization === 'BAR_HORIZONTAL';
      return html`<${BarChart}
        data=${data}
        layout=${isHorizontal ? 'HORIZONTAL' : 'VERTICAL'}
        stacked=${Boolean(configuration.stacked)}
        showDataLabels=${configuration.showDataLabels ?? widget.type === 'KPI'}
        colorName=${configuration.color}
      />`;
    }
    case 'PIE':
      return html`<${PieChart} data=${data} showCenterMetric=${configuration.showCenterMetric !== false} showDataLabels=${Boolean(configuration.showDataLabels)} />`;
    case 'TABLE':
      return html`<${TableWidget} data=${data} variant=${isReportVariant(widget) ? 'report' : undefined} />`;
    case 'TEXT':
      return html`<${TextWidget} markdown=${data.markdown} />`;
    case 'IFRAME':
      return html`<${IframeWidget} url=${data.url} title=${widget.title} isEditing=${Boolean(isEditing)} />`;
    case 'STAT_GROUP':
      return html`<${StatGroupWidget} data=${data} />`;
    case 'BAR_LIST':
      return html`<${BarListWidget} data=${data} />`;
    case 'DISTRIBUTION':
      return html`<${DistributionWidget} data=${data} />`;
    case 'FUNNEL_FLOW':
      return html`<${FunnelFlowWidget} data=${data} />`;
    case 'STAGE_FLOW':
      return html`<${StageFlowWidget} data=${data} />`;
    case 'GEO_MAP':
      return html`<${GeoMapWidget} data=${data} />`;
    default:
      return null;
  }
}

/**
 * Cartão no estilo de relatório (título serifado, cantos maiores, tabela limpa): widgets de relatório e os
 * indicadores educacionais de gráfico e tabela.
 * @param {DashboardWidget} widget
 */
export function isReportVariant(widget) {
  if (isReportWidgetType(widget.type)) return true;
  if (widget.type !== 'KPI') return false;
  const kind = getKpiMetric(widget.configuration?.metric)?.kind;
  return kind === 'SERIES' || kind === 'TABLE';
}

/**
 * Chave do relatório do cartão (relatório em cartão ou a métrica de um KPI de série), para subtítulo e descrição.
 * @param {DashboardWidget} widget
 */
function reportKeyOf(widget) {
  if (isReportWidgetType(widget.type)) return widget.configuration?.report;
  return widget.type === 'KPI' ? widget.configuration?.metric : undefined;
}

/**
 * Explicação do cartão no ícone de informação do cabeçalho (substitui o antigo texto "Ajuda" do painel).
 * @param {DashboardWidget} widget
 */
function reportDescriptionOf(widget) {
  const key = reportKeyOf(widget);
  if (!key) return null;
  if (isReportWidgetType(widget.type)) return hasTranslation(`reports.descriptions.${key}`) ? t(`reports.descriptions.${key}`) : null;
  return hasTranslation(`dashboards.metrics.${key}.description`) ? t(`dashboards.metrics.${key}.description`) : null;
}

/**
 * Mensagem de vazio: a qualidade do lead diz se faltam leads no período ou campos que o papel lê.
 * @param {DashboardWidget} widget @param {any} data
 */
function emptyMessageOf(widget, data) {
  if (widget.type === 'TEXT') return t('dashboards.widget.emptyText');
  if (LEAD_QUALITY_REPORTS.has(widget.configuration?.report)) {
    return data?.summary?.leads > 0 && data?.summary?.measures === 0 ? t('reports.empty.noMeasures') : t('reports.empty.noLeads');
  }
  return t('dashboards.widget.empty');
}

/**
 * @param {{ type: string }} props
 */
function WidgetSkeleton({ type }) {
  if (type === 'KPI' || type === 'AGGREGATE') {
    return html`<div class="widget-skeleton widget-skeleton-number"><${Skeleton} width=${96} height=${24} /><${Skeleton} width=${64} height=${12} /></div>`;
  }
  if (type === 'TABLE' || type === 'BAR_LIST' || type === 'UPCOMING_TABLE' || type === 'DISTRIBUTION') {
    return html`<div class="widget-skeleton widget-skeleton-table">${[0, 1, 2, 3].map((index) => html`<${Skeleton} key=${index} height=${14} />`)}</div>`;
  }
  return html`<div class="widget-skeleton"><${Skeleton} width=${120} height=${14} /></div>`;
}

/**
 * @param {{ icon: string, text: string, tone?: 'danger', action?: any }} props
 */
function WidgetMessage({ icon, text, tone, action }) {
  return html`<div class="widget-message" data-tone=${tone}>
    <${Icon} name=${icon} size=${16} />
    <span>${text}</span>
    ${action ?? null}
  </div>`;
}

/**
 * @param {{ widget: DashboardWidget, filters: DashboardRequestFilters, isEditing?: boolean, isSelected?: boolean, isPreview?: boolean,
 *   onSelect?: () => void, onEdit?: () => void, onDuplicate?: () => void, onRemove?: () => void,
 *   onGripPointerDown?: (event: PointerEvent) => void, onResizePointerDown?: (event: PointerEvent) => void, rowSpan?: number,
 *   moveTargets?: { key: string, title: string }[], onMoveToTab?: (tabKey: string) => void, dashboardId?: string | null }} props
 *   dashboardId: painel salvo do widget, que a receita do clique identifica
 */
export function WidgetCard({ widget, filters, isEditing = false, isSelected = false, isPreview = false, onSelect, onEdit, onDuplicate, onRemove,
  onGripPointerDown, onResizePointerDown, rowSpan, moveTargets = [], onMoveToTab, dashboardId = null }) {
  const { status, data, error, isComplete, reload } = useWidgetData(widget, filters);
  // no modo de edição nada é clicável (o clique seleciona o cartão) e o widget novo ainda não tem id
  const drill = useMemo(
    () => (isEditing || isPreview || !data || status === 'error' ? null : createDrillController({ dashboardId, widget, filters, data })),
    [isEditing, isPreview, data, status, dashboardId, widget.id, widget.title, filters],
  );
  const menuAnchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const title = widget.title || t('dashboards.widget.untitled');
  const isTextWidget = widget.type === 'TEXT';
  // texto e página incorporada não têm dados para exportar
  const isContentWidget = isTextWidget || widget.type === 'IFRAME';
  const isReport = isReportVariant(widget);
  const otherTabs = moveTargets.filter((tab) => tab.key !== (widget.tabId ?? moveTargets[0]?.key));
  const hasOwnFilters = hasWidgetFilters(widget.configuration?.widgetFilters);
  // subtítulo também nos KPIs de série no estilo de relatório (matrículas por semana)
  const subtitle = isReport && data && status !== 'error' ? getReportSubtitle(reportKeyOf(widget), data) : null;
  const description = isReport && !isEditing ? reportDescriptionOf(widget) : null;

  const exportCsv = () => {
    setIsMenuOpen(false);
    if (!data) return;
    try {
      downloadCsv(widgetDataToCsv(data), toFileName(title));
      showSuccessSnackbar(t('dashboards.widget.exported'));
    } catch (exportError) {
      showErrorSnackbar(exportError);
    }
  };

  let body;
  if (!isComplete) {
    body = html`<${WidgetMessage} icon="IconAdjustments" text=${t('dashboards.widget.incomplete')}
      action=${isEditing && onEdit ? html`<button type="button" class="widget-message-link" onClick=${onEdit}>${t('dashboards.widget.configure')}</button>` : null} />`;
  } else if (status === 'error' && widget.type === 'GEO_MAP' && /** @type {any} */ (error)?.code === 'PERMISSION_DENIED') {
    // o papel não lê os endereços (contrato 07, seção 9): explica em vez de mostrar o erro técnico
    body = html`<${WidgetMessage} icon="IconLock" text=${t('geoMap.noAddressPermission')} />`;
  } else if (status === 'error') {
    body = html`<${WidgetMessage} icon="IconAlertTriangle" tone="danger" text=${`${t('dashboards.widget.error')} ${getErrorMessage(error)}`}
      action=${html`<button type="button" class="widget-message-link" onClick=${reload}>${t('dashboards.widget.retry')}</button>`} />`;
  } else if (!data) {
    body = html`<${WidgetSkeleton} type=${widget.type} />`;
  } else if (isWidgetDataEmpty(data)) {
    body = html`<${WidgetMessage} icon="IconChartBar" text=${emptyMessageOf(widget, data)} />`;
  } else {
    body = html`<${DrilldownContext.Provider} value=${drill}>
      <${WidgetVisualization} widget=${widget} data=${data} rowSpan=${rowSpan ?? widget.rowSpan} isEditing=${isEditing} />
    <//>`;
  }

  const showMenu = !isPreview && (isEditing || (data && !isContentWidget));
  return html`<article
    class="widget-card"
    data-type=${widget.type}
    data-editing=${isEditing ? 'true' : undefined}
    data-selected=${isSelected ? 'true' : undefined}
    data-loading=${status === 'loading' && data ? 'true' : undefined}
    data-hide-header=${isTextWidget && !isEditing ? 'true' : undefined}
    data-variant=${isReport ? 'report' : undefined}
    aria-label=${title}
    onClick=${isEditing && onSelect ? (/** @type {MouseEvent} */ event) => {
      if (/** @type {HTMLElement} */ (event.target).closest('button, a, [data-dropdown-surface]')) return;
      onSelect();
    } : undefined}
  >
    <header class="widget-card-header">
      ${isEditing && onGripPointerDown
        ? html`<button type="button" class="widget-card-grip" aria-label=${t('dashboards.edit.move')} title=${t('dashboards.edit.move')}
            onPointerDown=${onGripPointerDown}><${Icon} name="IconGripVertical" size=${14} /></button>`
        : null}
      <h3 class="widget-card-title" title=${title}>${title}</h3>
      ${description ? html`<${WidgetDescription} text=${description} />` : null}
      ${hasOwnFilters
        ? html`<span class="widget-card-filter-badge" title=${t('dashboardParity.widgetFilters.badge')} aria-label=${t('dashboardParity.widgetFilters.badge')}>
            <${Icon} name="IconFilterCog" size=${14} />
          </span>`
        : null}
      ${status === 'loading' && data ? html`<span class="widget-card-refreshing" aria-hidden="true"></span>` : null}
      ${showMenu
        ? html`<span class="widget-card-menu" ref=${menuAnchorRef}>
            <${LightIconButton}
              icon="IconDotsVertical"
              accent="tertiary"
              ariaLabel=${t('dashboards.widget.menu')}
              active=${isMenuOpen}
              onClick=${() => setIsMenuOpen(!isMenuOpen)}
            />
          </span>`
        : null}
      <${Dropdown} anchorRef=${menuAnchorRef} isOpen=${isMenuOpen} onClose=${() => setIsMenuOpen(false)} placement="bottom-end" width=${200}>
        <${DropdownMenuItems}>
          ${!isContentWidget ? html`<${MenuItem} icon="IconFileSpreadsheet" text=${t('dashboards.widget.exportCsv')} disabled=${!data} onClick=${exportCsv} />` : null}
          ${isEditing
            ? html`
                ${!isContentWidget ? html`<${DropdownMenuSeparator} />` : null}
                <${MenuItem} icon="IconPencil" text=${t('dashboards.widget.edit')} onClick=${() => { setIsMenuOpen(false); onEdit?.(); }} />
                <${MenuItem} icon="IconCopyPlus" text=${t('dashboards.widget.duplicate')} onClick=${() => { setIsMenuOpen(false); onDuplicate?.(); }} />
                ${onMoveToTab
                  ? otherTabs.map((tab) => html`<${MenuItem} key=${`move-${tab.key}`} icon="IconArrowRight" text=${t('dashboardParity.tabs.moveWidgetTo', { title: tab.title })}
                      onClick=${() => { setIsMenuOpen(false); onMoveToTab(tab.key); }} />`)
                  : null}
                <${MenuItem} icon="IconTrash" accent="danger" text=${t('dashboards.widget.remove')} onClick=${() => { setIsMenuOpen(false); onRemove?.(); }} />
              `
            : null}
        <//>
      <//>
    </header>
    ${subtitle ? html`<p class="widget-card-subtitle" title=${subtitle}>${subtitle}</p>` : null}
    <div class="widget-card-body">${body}</div>
    ${isEditing && onResizePointerDown
      ? html`<button type="button" class="widget-card-resize-handle" aria-label=${t('dashboards.edit.resize')} title=${t('dashboards.edit.resize')}
          onPointerDown=${onResizePointerDown}></button>`
      : null}
  </article>`;
}
